import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';

export interface AiSession {
  id: number;
  uuid: string;
  title: string;
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AiChatMessage {
  id: number;
  sessionId: number;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  actionMetadata?: {
    actionCard?: {
      type:
        | 'CONTRACT_DETAILS'
        | 'APPROVAL_STATUS'
        | 'CONTRACT_LIST'
        | 'PNL_SUMMARY';
      title: string;
      payload: any;
    };
    chartData?: {
      type: 'bar' | 'pie' | 'line' | 'metric';
      title: string;
      xAxisKey?: string;
      series?: { dataKey: string; name: string; color?: string }[];
      data: any[];
    };
    insights?: string[];
    suggestedFollowUps?: string[];
  } | null;
  createdAt: string;
  /** Client-only presentation state for failed chat requests. */
  isError?: boolean;
  retryPrompt?: string;
  requestId?: string;
  retryRequestId?: string;
}

export interface AiSessionDetail extends AiSession {
  messages: AiChatMessage[];
}

export type AiProvider = 'openai' | 'anthropic' | 'gemini';

export const aiAssistantApi = createApi({
  reducerPath: 'aiAssistant',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['AiSessions', 'AiSessionMessages'],
  endpoints: builder => ({
    getAiSessions: builder.query<AiSession[], void>({
      query: () => ({
        url: '/ai-assistant/sessions',
        method: 'GET',
      }),
      providesTags: ['AiSessions'],
    }),

    getActiveAiModules: builder.query<string[], void>({
      query: () => ({
        url: '/ai-assistant/active-modules',
        method: 'GET',
      }),
    }),

    getAiProviders: builder.query<AiProvider[], void>({
      query: () => ({ url: '/ai-assistant/providers', method: 'GET' }),
    }),

    createAiSession: builder.mutation<AiSession, { title?: string }>({
      query: body => ({
        url: '/ai-assistant/sessions',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AiSessions'],
    }),

    getAiSessionById: builder.query<AiSessionDetail, number>({
      query: id => ({
        url: `/ai-assistant/sessions/${id}`,
        method: 'GET',
      }),
      providesTags: (_r, _e, id) => [{ type: 'AiSessionMessages', id }],
    }),

    updateAiSessionTitle: builder.mutation<void, { id: number; title: string }>(
      {
        query: ({ id, title }) => ({
          url: `/ai-assistant/sessions/${id}`,
          method: 'PATCH',
          body: { title },
        }),
        invalidatesTags: ['AiSessions'],
      },
    ),

    deleteAiSession: builder.mutation<void, number>({
      query: id => ({
        url: `/ai-assistant/sessions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AiSessions'],
    }),

    sendAiChatMessage: builder.mutation<
      AiChatMessage,
      {
        sessionId: number;
        prompt: string;
        requestId: string;
        provider?: AiProvider;
      }
    >({
      query: ({ sessionId, prompt, requestId, provider }) => ({
        url: `/ai-assistant/sessions/${sessionId}/chat`,
        method: 'POST',
        body: { prompt, requestId, provider },
      }),
      invalidatesTags: (_result, error, { sessionId }) =>
        error
          ? []
          : [{ type: 'AiSessionMessages', id: sessionId }, 'AiSessions'],
    }),
  }),
});

export const {
  useGetAiSessionsQuery,
  useGetActiveAiModulesQuery,
  useGetAiProvidersQuery,
  useCreateAiSessionMutation,
  useGetAiSessionByIdQuery,
  useUpdateAiSessionTitleMutation,
  useDeleteAiSessionMutation,
  useSendAiChatMessageMutation,
} = aiAssistantApi;
